/**
 * The bubble system's per-world state (state.ts `BubbleSystem`): the C64's zero-page variables for the
 * bubbles, set up as the level setup does (D_3A6C) the first time the world's bubbles run. It lives on
 * the world as `world.bubbleSystem`, plain data that serialises with the world.
 */

import { rngOf } from '../random';
import { FIRE_LINES, SPAWN_FIRST } from './tuning';
import type { World } from '../types';
import type { BubbleSystem } from './state';

export function systemOf(world: World): BubbleSystem {
  world.bubbleSystem ??= newSystem(world);
  return world.bubbleSystem;
}

/**
 * D_3A6C: the level's flags ($B631 low nibble) switch the spawner on (EXTEND) and pick its specials
 * (water 1, fire 2, lightning 4 into $0CC2). On EXTEND levels a coin flip decides whether letters come:
 * heads leaves $0C9C a JMP (no letters); tails makes it a BIT, with $4C = rand & 3 (so 1-4 letters).
 */
function newSystem(world: World): BubbleSystem {
  const specials = world.level.specials;
  const on = specials.includes('extend');
  let letters = 0;
  if (on) {
    const rng = rngOf(world);
    if ((rng.int(256) & 1) === 0) letters = (rng.int(256) & 3) + 1;
  }
  const mask = (specials.includes('water') ? 1 : 0) | (specials.includes('fire') ? 2 : 0)
    | (specials.includes('lightning') ? 4 : 0);
  return {
    chain: [0, 0], chainSeen: [0, 0], spawnOn: on, spawnTimer: SPAWN_FIRST, spawnMask: mask,
    letters, letterSeed: 0, cleared: false, water: null, drops: [], bolts: [], extendFood: false,
    fires: Array.from({ length: FIRE_LINES }, () => ({ col: 0, row: 0, len: 0, timer: -1, credit: -1 as const })),
  };
}
